import React, { useEffect, useState } from "react";
import { ReportAPI } from "../../api/apiClient";
import { DprReportRow } from "../../types/dpr";

interface Props {
  projectId: number;
}

export default function DprReport({ projectId }: Props) {
  const [rows, setRows] = useState<DprReportRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [reportDate, setReportDate] = useState<string>("");

  const lvl = 2;
  const wbs = projectId ? -1 * projectId : 0;

  useEffect(() => {
    if (!projectId) return;

    const loadReport = async () => {
      try {
        setLoading(true);
        setError("");

        const [data, date] = await Promise.all([
          ReportAPI.getDprReport(wbs, lvl),
          ReportAPI.getLatestDrsDate(projectId),
        ]);

        setRows(data);
        setReportDate(date);
      } catch (err) {
        console.error(err);
        setError("Failed to load DPR report");
      } finally {
        setLoading(false);
      }
    };

    loadReport();
  }, [projectId]);

  const grouped = rows.reduce<Record<string, DprReportRow[]>>((acc, row) => {
    const key = row.levelName || "Others";
    acc[key] = acc[key] || [];
    acc[key].push(row);
    return acc;
  }, {});

  const formattedDate = reportDate
    ? new Date(reportDate).toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
    : "";

  return (
    <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
      <div className="bg-[#143695] text-white px-4 py-3 font-semibold flex justify-between">
        <span>DAILY PROGRESS REPORT (DPR)</span>
        <span className="text-sm">{formattedDate}</span>
      </div>

      <div className="p-4 space-y-8">
        {loading && (
          <div className="text-gray-500 text-sm">Loading DPR…</div>
        )}

        {error && (
          <div className="text-red-600 text-sm">{error}</div>
        )}

        {!loading && rows.length === 0 && (
          <div className="text-gray-500 text-center py-6">
            No data available
          </div>
        )}

        {!loading && rows.length > 0 && (
          <>
            {/* ===================== ONGOING WORKS ===================== */}
            <div className="rounded-xl border bg-white shadow-sm">
              <div className="bg-[#1788ac] text-white px-4 py-2 text-sm font-semibold rounded-t-xl">
                ONGOING WORKS
              </div>

              {Object.entries(grouped).map(([levelName, works]) => (
                <div key={levelName}>
                  <div className="px-4 py-3 text-sm font-semibold text-gray-700 bg-gray-50 border-b">
                    {levelName}
                  </div>

                  <div className="divide-y divide-gray-100 px-4">
                    {works.map((w, idx) => {
                      const activityPercent = (w.trxRatio ?? 0) * 100;

                      return (
                        <div
                          key={idx}
                          className="flex justify-between py-3"
                        >
                          <div className="text-sm font-medium text-gray-800">
                            {w.work}
                          </div>

                          <div className="text-right">
                            <div className="text-lg font-semibold text-gray-900">
                              {w.achieved.toFixed(2)}{" "}
                              <span className="text-sm text-gray-600">
                                [{activityPercent.toFixed(2)} %]
                              </span>
                            </div>

                            <div className="text-xs text-gray-500">
                              of {w.totalQty?.toFixed(2)} {w.unitName}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>

            {/* ===================== CUMULATIVE ===================== */}
            <div className="rounded-xl border bg-white shadow-sm">
              <div className="bg-[#1788ac] text-white px-4 py-2 text-sm font-semibold rounded-t-xl">
                CUMULATIVE PREVIOUS AND ON-DATE
              </div>

              {Object.entries(grouped).map(([levelName, works]) => {
                const previousPct = Math.max(
                  0,
                  Math.min(
                    100,
                    works.reduce(
                      (sum, w) => sum + (w.previous ?? 0),
                      0
                    ) * 100
                  )
                );

                const currentPct = Math.max(
                  0,
                  Math.min(
                    100,
                    works.reduce(
                      (sum, w) => sum + (w.percentAchieved ?? 0),
                      0
                    ) * 100
                  )
                );

                return (
                  <div
                    key={levelName}
                    className="px-4 py-5 border-b last:border-b-0"
                  >
                    <div className="text-sm font-semibold text-gray-700 mb-5">
                      {levelName}
                    </div>

                    {/* Previous */}
                    <div className="mb-5">
                      <div className="text-xs text-gray-500 mb-1">
                        Previous
                      </div>

                      <div className="relative w-full bg-gray-200 rounded-full h-4 overflow-hidden">
                        <div
                          className="h-4 bg-green-600"
                          style={{
                            width: `${Math.round(previousPct)}%`,
                          }}
                        />
                      </div>

                      <div className="text-xs font-semibold text-green-600 mt-1">
                        {Math.round(previousPct)}%
                      </div>
                    </div>

                    {/* Current */}
                    <div>
                      <div className="text-xs text-gray-500 mb-1">
                        On-Date / Cumulative
                      </div>

                      <div className="relative w-full bg-gray-200 rounded-full h-4 overflow-hidden">
                        <div
                          className="h-4 bg-emerald-500"
                          style={{
                            width: `${Math.round(currentPct)}%`,
                          }}
                        />
                      </div>

                      <div className="text-xs font-semibold text-emerald-600 mt-1">
                        {Math.round(currentPct)}%
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>
    </div>
  );
}